import React from 'react';
import { useTranslation } from 'react-i18next';
import { css, keyframes } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../styles';

interface OnboardingTrialTimelineProps {
  trialDays?: number;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ICON_SIZE = 32;
const LINE_WIDTH = 2;

const iconCircleCss = css`
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  min-width: ${ICON_SIZE}px;
  border-radius: 50%;
  background: ${colors.skyAir};
  color: ${colors.petrolBlue};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  html[data-theme='dark'] & {
    background: ${colors.tungsten};
    color: ${colors.skyBlue};
  }
`;

const connectorLineCss = css`
  width: ${LINE_WIDTH}px;
  flex: 1;
  min-height: 8px;
  background: ${colors.cloud};
  align-self: center;

  html[data-theme='dark'] & {
    background: ${colors.tungsten};
  }
`;

const titleCss = css`
  ${fontStyles.lion};
  color: ${colors.liquorice};
  margin: 0;

  html[data-theme='dark'] & {
    color: ${colors.sugar};
  }
`;

const descriptionCss = css`
  ${fontStyles.dog};
  color: ${colors.silver};
  margin: 0;

  html[data-theme='dark'] & {
    color: ${colors.cloud};
  }
`;

export const OnboardingTrialTimeline: React.FC<OnboardingTrialTimelineProps> = ({ trialDays = 7 }) => {
  const { t } = useTranslation();
  const steps = [
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
        </svg>
      ),
      title: t('subscription.timelineToday'),
      description: t('subscription.timelineTodayDesc'),
      titleOverride: null,
      iconOverride: null,
    },
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
      ),
      title: t('subscription.timelineDayN', { day: trialDays - 1 }),
      description: t('subscription.timelineDay2Desc'),
      titleOverride: null,
      iconOverride: null,
    },
    {
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24">
          <path
            fill="white"
            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
          />
        </svg>
      ),
      title: t('subscription.timelineDayN', { day: trialDays }),
      description: t('subscription.timelineDayNDesc'),
      titleOverride: css`
        ${fontStyles.lion};
        color: ${colors.petrolBlue};
        margin: 0;

        html[data-theme='dark'] & {
          color: ${colors.skyBlue};
        }
      `,
      iconOverride: css`
        width: ${ICON_SIZE}px;
        height: ${ICON_SIZE}px;
        min-width: ${ICON_SIZE}px;
        border-radius: 50%;
        background: ${colors.petrolBlue};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        html[data-theme='dark'] & {
          background: ${colors.skyBlue};
          color: ${colors.liquorice};
        }
      `,
    },
  ];

  return (
    <div
      css={css`
        padding: ${spacing.deci} 0;
        animation: ${fadeIn} 0.4s ease-out 0.1s backwards;
      `}
    >
      {steps.map((step, index) => (
        <div
          key={index}
          css={css`
            display: flex;
            flex-direction: row;
            align-items: stretch;
            gap: 12px;
          `}
        >
          {/* Icon column: circle + connector line */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <div css={step.iconOverride || iconCircleCss}>{step.icon}</div>
            {index < steps.length - 1 && <div css={connectorLineCss} />}
          </div>

          {/* Text column */}
          <div
            css={css`
              padding-bottom: ${index < steps.length - 1 ? spacing.deci : '0'};
            `}
          >
            <p css={step.titleOverride || titleCss}>{step.title}</p>
            <p css={descriptionCss}>{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
